import { useEffect, useState } from "react";
import type { TFunction } from "i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

/**
 * Pre-populated DGI Tabla 4.3 motivos shown in the dropdown so users
 * pick from a short list rather than free-typing codes. Add more as
 * we encounter them in production rejection patterns. Shared between
 * the recibida detail card and the list-row Rechazar action.
 */
export const RECIBIDA_REJECT_MOTIVOS: Array<{ code: string; label: string }> = [
  { code: "C90", label: "Datos del receptor no coinciden" },
  { code: "C91", label: "Items no coinciden con la operación" },
  { code: "C92", label: "Montos no coinciden" },
  { code: "C93", label: "Operación no reconocida" },
];

export interface RejectMotivo {
  code: string;
  description: string;
  detail?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (motivos: RejectMotivo[]) => void;
  submitting: boolean;
  t: TFunction;
}

/**
 * Modal used in two places to capture an ACKCFE rejection motivo:
 *   - Recibida detail page (full context, big card)
 *   - Recibidas list row "Rechazar" action (quick path)
 *
 * Sends exactly one motivo back to the caller via `onSubmit`. The
 * backend accepts up to 10, but in practice rejection emails carry a
 * single code + free-text detail, so the UI matches that.
 */
export function RecibidaRejectDialog({
  open,
  onOpenChange,
  onSubmit,
  submitting,
  t,
}: Props): JSX.Element {
  const [selectedCode, setSelectedCode] = useState<string>(
    RECIBIDA_REJECT_MOTIVOS[0]!.code,
  );
  const [customCode, setCustomCode] = useState("");
  const [detail, setDetail] = useState("");

  // Reset state every time the dialog reopens so a previous attempt's
  // typing doesn't bleed into the next row's rejection.
  useEffect(() => {
    if (open) {
      setSelectedCode(RECIBIDA_REJECT_MOTIVOS[0]!.code);
      setCustomCode("");
      setDetail("");
    }
  }, [open]);

  const standard = RECIBIDA_REJECT_MOTIVOS.find((m) => m.code === selectedCode) ?? null;
  const isCustom = selectedCode === "__custom__";

  const handleSubmit = (): void => {
    const code = isCustom ? customCode.trim() : standard?.code ?? "";
    const description = isCustom
      ? detail.trim() ||
        t("recibidas.ackCfeRejectMotivoCustomDefault", "Otro motivo")
      : standard?.label ?? "";
    if (!code || !description) return;
    onSubmit([
      {
        code,
        description,
        detail: isCustom ? undefined : detail.trim() || undefined,
      },
    ]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("recibidas.ackCfeRejectDialogTitle", "Rechazar comprobante")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "recibidas.ackCfeRejectDialogDesc",
              "El emisor recibirá tu rechazo con el motivo seleccionado.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("recibidas.ackCfeRejectMotivoLabel", "Motivo")}</Label>
            <Select value={selectedCode} onValueChange={setSelectedCode}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECIBIDA_REJECT_MOTIVOS.map((m) => (
                  <SelectItem key={m.code} value={m.code}>
                    {m.code} — {m.label}
                  </SelectItem>
                ))}
                <SelectItem value="__custom__">
                  {t("recibidas.ackCfeRejectMotivoCustom", "Otro (especificar código)")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isCustom && (
            <div className="space-y-1.5">
              <Label>{t("recibidas.ackCfeRejectCodeLabel", "Código DGI")}</Label>
              <Input
                value={customCode}
                maxLength={8}
                placeholder="C99"
                onChange={(e) => setCustomCode(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>
              {t(
                "recibidas.ackCfeRejectDetailLabel",
                "Detalle (opcional, mostrado al emisor)",
              )}
            </Label>
            <Textarea
              value={detail}
              maxLength={500}
              rows={3}
              placeholder={t(
                "recibidas.ackCfeRejectDetailPlaceholder",
                "Describí brevemente el motivo del rechazo…",
              )}
              onChange={(e) => setDetail(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t("pages.common.cancel", "Cancelar")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (isCustom && !customCode.trim())}
          >
            {t("recibidas.ackCfeRejectSubmit", "Rechazar comprobante")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
